
import React from 'react'
import { motion } from 'motion/react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { Link } from '@tanstack/react-router'
import { prefetchLibraryModels } from '@/lib/canvas/utils/prefetchCanvasData'

import { CanvasContextMenu } from '../context-menu'
import type { Canvas } from '@/hooks/useCanvas'
import type { ContextMenuType, CanvasAction } from '../context-menu'

interface CanvasCardProps {
  canvas: Canvas
  contextMenuType: ContextMenuType
  onAction: (action: CanvasAction, canvas: Canvas) => void
  showProject?: boolean
  index?: number
}

const formatRelativeDate = (dateString: string): string => {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export const CanvasCard: React.FC<CanvasCardProps> = ({
  canvas,
  contextMenuType,
  onAction,
  showProject = false,
  index = 0,
}) => {
  const getStatusText = () => {
    switch (contextMenuType) {
      case 'recents':
        return showProject ? (canvas.project?.name || 'Draft') : 'RECENT'
      case 'drafts':
        return 'DRAFT'
      case 'trash':
        return canvas.deletedAt ? `DELETED ${new Date(canvas.deletedAt).toLocaleDateString()}` : 'DELETED'
      case 'project':
        return canvas.project?.name || 'Draft'
      default:
        return ''
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.97 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.25, delay: index * 0.04, ease: 'easeOut' }}
      className="w-full"
    >
      <div className="group" style={{ outline: 'none' }}>
        <Link to="/canvas/$id" params={{ id: canvas.uuid }} className="outline-none focus-visible:outline-none" aria-label={`Open ${canvas.title}`} onMouseEnter={prefetchLibraryModels}>
          <Card className="border-2 border-transparent group-hover:border-foreground relative mb-2 aspect-video cursor-pointer overflow-hidden transition-colors duration-150">
            {canvas.thumbnailUrl ? (
              <img
                src={canvas.thumbnailUrl}
                alt={canvas.title}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center bg-muted/30">
                <Icon name="file" size="lg" className="text-muted-foreground/40" />
              </div>
            )}
          </Card>
        </Link>
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <h4 className="truncate text-sm font-medium">
              {canvas.title}
            </h4>
            <CanvasContextMenu
              canvas={canvas}
              contextMenuType={contextMenuType}
              onAction={onAction}
            >
              <Button
                variant="ghost"
                size="icon-xs"
                className="opacity-0 transition-opacity group-hover:opacity-100"
                aria-label="Session options"
              >
                <Icon name="more-horizontal" size="sm" />
              </Button>
            </CanvasContextMenu>
          </div>
          <div className="text-muted-foreground text-xxs flex items-center gap-1.5">
            <span>{formatRelativeDate(canvas.updatedAt)}</span>
            {getStatusText() && (
              <>
                <span className="bg-border h-3 w-px"></span>
                <span className="rounded-sm bg-muted px-1.5 py-0.5 text-muted-foreground">{getStatusText()}</span>
              </>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  )
}